import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText, CheckCircle2, AlertTriangle, RefreshCw, Save, Upload, History } from "lucide-react";
import { api, errText } from "../../lib/api";

const card = "bg-white border border-border rounded-2xl p-5";
const input = "w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/60";
const btn = "text-xs font-bold px-4 py-2 rounded-full transition-colors";

function Editor({ slug, onClose, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadVersions = useCallback(() => api.get(`/admin/content/${slug}/versions`).then(({ data }) => setVersions(data)), [slug]);

  useEffect(() => {
    loadVersions().then(async () => {
      const { data: list } = await api.get(`/admin/content/${slug}/versions`);
      const base = list.find((v) => v.status === "DRAFT") || list.find((v) => v.status === "PUBLISHED") || list[0];
      if (!base) { setDoc({ title: "", lead: "", sections: [{ heading: "", items: [""] }], change_note: "" }); return; }
      const { data } = await api.get(`/admin/content/${slug}/versions/${base.version}`);
      setDoc({ title: data.title, lead: data.lead || "", sections: data.sections, change_note: "", baseVersion: base.version, baseStatus: base.status });
    });
  }, [slug, loadVersions]);

  if (!doc) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;

  const setSection = (i, patch) => setDoc({ ...doc, sections: doc.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const saveDraft = async () => {
    setBusy(true);
    try {
      const payload = { title: doc.title, lead: doc.lead, change_note: doc.change_note,
        sections: doc.sections.map((s) => ({ heading: s.heading, items: (Array.isArray(s.items) ? s.items : String(s.items).split("\n")).map((x) => String(x).trim()).filter(Boolean) })) };
      const { data } = await api.put(`/admin/content/${slug}/draft`, payload);
      toast.success(`Entwurf gespeichert (Version ${data.version})`);
      await loadVersions();
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const publish = async (version) => {
    setBusy(true);
    try {
      await api.post(`/admin/content/${slug}/publish`, { version, legally_reviewed: reviewed });
      toast.success(`Version ${version} veröffentlicht`);
      await loadVersions();
      onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const draft = versions.find((v) => v.status === "DRAFT");

  return (
    <div className={card} data-testid={`content-editor-${slug}`}>
      <div className="flex items-center justify-between">
        <p className="font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-accent" /> Bearbeiten: {slug}</p>
        <button onClick={onClose} className="text-xs font-semibold text-muted-foreground hover:text-foreground">Schließen</button>
      </div>
      <div className="mt-4 space-y-3">
        <input data-testid="editor-title" className={input} placeholder="Titel" value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
        <textarea data-testid="editor-lead" className={`${input} min-h-16`} placeholder="Einleitung (optional)" value={doc.lead} onChange={(e) => setDoc({ ...doc, lead: e.target.value })} />
        {doc.sections.map((s, i) => (
          <div key={i} className="border border-border rounded-xl p-3 space-y-2">
            <div className="flex gap-2">
              <input className={input} placeholder="Überschrift" value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} />
              <button onClick={() => setDoc({ ...doc, sections: doc.sections.filter((_, j) => j !== i) })} className="text-destructive text-xs font-bold shrink-0 px-2">Entfernen</button>
            </div>
            <textarea className={`${input} min-h-24 font-mono text-xs`} placeholder="Ein Absatz pro Zeile. Links: [Text](/pfad)"
              value={Array.isArray(s.items) ? s.items.join("\n") : s.items} onChange={(e) => setSection(i, { items: e.target.value.split("\n") })} />
          </div>
        ))}
        <button onClick={() => setDoc({ ...doc, sections: [...doc.sections, { heading: "", items: [""] }] })} className="text-xs font-bold text-accent hover:underline">+ Abschnitt hinzufügen</button>
        <input data-testid="editor-change-note" className={input} placeholder="Änderungsnotiz (was wurde geändert?)" value={doc.change_note} onChange={(e) => setDoc({ ...doc, change_note: e.target.value })} />
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button data-testid="editor-save-draft" onClick={saveDraft} disabled={busy} className={`${btn} bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50`}><Save className="w-3.5 h-3.5" /> Entwurf speichern</button>
          {draft && (
            <>
              <label className="flex items-center gap-1.5 text-xs font-semibold">
                <input type="checkbox" data-testid="editor-reviewed-check" className="accent-[#FF6B35]" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> juristisch geprüft
              </label>
              <button data-testid="editor-publish" onClick={() => publish(draft.version)} disabled={busy} className={`${btn} bg-accent text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50`}><Upload className="w-3.5 h-3.5" /> Version {draft.version} veröffentlichen</button>
            </>
          )}
        </div>
      </div>
      <div className="mt-5 border-t border-border pt-4">
        <p className="text-xs font-bold flex items-center gap-1.5 text-muted-foreground"><History className="w-3.5 h-3.5" /> Versionen</p>
        <div className="mt-2 space-y-1.5">
          {versions.map((v) => (
            <div key={v.version} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-bold">v{v.version}</span>
              <span className={`px-2 py-0.5 rounded-md font-semibold ${v.status === "PUBLISHED" ? "bg-green-100 text-green-700" : v.status === "DRAFT" ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>{v.status === "PUBLISHED" ? "Veröffentlicht" : v.status === "DRAFT" ? "Entwurf" : "Archiviert"}</span>
              <span className={`px-2 py-0.5 rounded-md font-semibold ${v.review_status === "REVIEWED" ? "bg-green-50 text-green-700" : "bg-secondary text-muted-foreground"}`}>{v.review_status === "REVIEWED" ? "jur. geprüft" : "ungeprüft"}</span>
              <span className="text-muted-foreground truncate max-w-[300px]">{v.change_note}</span>
              {v.status === "ARCHIVED" && <button onClick={() => publish(v.version)} className="text-accent font-bold hover:underline">Wiederherstellen</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ImprintCard() {
  const [imprint, setImprint] = useState(null);
  const FIELDS = [["company_name", "Name des Anbieters"], ["address_line1", "Adresszusatz / c/o"], ["address_line2", "Straße und Hausnummer"],
    ["zip_city", "PLZ und Ort"], ["country", "Land"], ["represented_by", "Vertreten durch"], ["email", "E-Mail"], ["phone", "Telefon (optional)"],
    ["vat_id", "USt-IdNr."], ["register_entry", "Registereintrag (optional)"], ["content_responsible", "Verantwortlich § 18 Abs. 2 MStV"], ["dispute_note", "Hinweis Verbraucherstreitbeilegung"]];

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setImprint(data.imprint || {})); }, []);
  if (!imprint) return <div className="h-32 bg-muted rounded-2xl animate-pulse" />;

  const save = async () => {
    try {
      const { data } = await api.put("/admin/content/config/imprint", imprint);
      toast.success(data.missing?.length ? `Gespeichert — es fehlen noch: ${data.missing.join(", ")}` : "Impressumsdaten gespeichert");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className={card} data-testid="imprint-config-card">
      <p className="font-bold text-sm">Impressumsdaten (zentral für Web & App)</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {FIELDS.map(([k, l]) => (
          <div key={k} className={k === "dispute_note" ? "sm:col-span-2" : ""}>
            <label className="text-[11px] font-semibold text-muted-foreground block mb-1">{l}</label>
            <input data-testid={`imprint-${k}`} className={input} value={imprint[k] || ""} onChange={(e) => setImprint({ ...imprint, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      <button data-testid="imprint-save-btn" onClick={save} className={`${btn} bg-primary text-primary-foreground hover:opacity-90 mt-4`}>Speichern</button>
    </div>
  );
}

function ConfigCard() {
  const [settings, setSettings] = useState(null);
  const [methods, setMethods] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data));
    api.get("/config/public").then(({ data }) => setMethods(data.payment_methods));
  }, []);
  if (!settings) return <div className="h-32 bg-muted rounded-2xl animate-pulse" />;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        buyer_fee_percent: settings.buyer_fee_percent, buyer_fee_fixed: settings.buyer_fee_fixed,
        protection_days: settings.protection_days, offer_reservation_minutes: settings.offer_reservation_minutes,
        shipping_deadline_days: Number(settings.shipping_deadline_days) || 3,
        packstation_enabled: !!settings.packstation_enabled,
      });
      toast.success("Konfiguration gespeichert");
    } catch (e) { toast.error(errText(e)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/content/config/payment-methods/sync");
      setMethods(data.payment_methods);
      toast.success("Zahlungsmethoden von Stripe synchronisiert");
    } catch (e) { toast.error(errText(e)); } finally { setSyncing(false); }
  };

  return (
    <div className={card} data-testid="checkout-config-card">
      <p className="font-bold text-sm">Versand & Checkout (zentral für Web & App)</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <div>
          <label className="text-[11px] font-semibold text-muted-foreground block mb-1">Versandfrist (Kalendertage)</label>
          <input data-testid="config-shipping-deadline" type="number" min="1" max="14" className={input}
            value={settings.shipping_deadline_days ?? 3} onChange={(e) => setSettings({ ...settings, shipping_deadline_days: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold mt-5">
          <input type="checkbox" data-testid="config-packstation-toggle" className="accent-[#FF6B35] w-4 h-4" checked={!!settings.packstation_enabled}
            onChange={(e) => setSettings({ ...settings, packstation_enabled: e.target.checked })} /> DHL Packstation im Checkout anbieten
        </label>
      </div>
      <button data-testid="config-save-btn" onClick={save} className={`${btn} bg-primary text-primary-foreground hover:opacity-90 mt-4`}>Speichern</button>
      <div className="mt-5 border-t border-border pt-4">
        <p className="text-xs font-bold">Zahlungsmethoden (aus Stripe)</p>
        <p className="text-xs text-muted-foreground mt-1">{methods?.map((m) => m.label).join(" · ") || "—"}</p>
        <button data-testid="payment-sync-btn" onClick={sync} disabled={syncing} className={`${btn} border border-border hover:bg-secondary mt-3 flex items-center gap-1.5`}>
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Von Stripe synchronisieren
        </button>
      </div>
    </div>
  );
}

export default function ContentTab() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/admin/content").then(({ data }) => setData(data)).catch(() => setData({ docs: [], checklist: {} })), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  const cl = data.checklist || {};

  return (
    <div className="space-y-5" data-testid="admin-content-tab">
      {(cl.imprint_missing?.length > 0 || cl.unreviewed_published?.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="content-checklist">
          <p className="text-sm font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Prüfliste vor dem Livegang</p>
          <ul className="mt-2 space-y-1 text-xs text-foreground/80">
            {cl.imprint_missing?.map((m) => <li key={m}>· Impressum: {m} fehlt</li>)}
            {cl.unreviewed_published?.map((s) => <li key={s}>· „{s}" ist veröffentlicht, aber noch nicht anwaltlich geprüft</li>)}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-2">{cl.note}</p>
        </div>
      )}
      <div className={card}>
        <p className="font-bold text-sm">Rechtstexte (versioniert, zentral für Web & App)</p>
        <div className="mt-3 divide-y divide-border">
          {data.docs?.map((d) => (
            <div key={d.slug} className="flex flex-wrap items-center gap-3 py-3" data-testid={`content-doc-${d.slug}`}>
              <FileText className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-semibold">{d.title}</p>
                <p className="text-xs text-muted-foreground">/{d.slug} · veröffentlicht: v{d.published_version || "—"}{d.draft ? ` · Entwurf: v${d.draft.version}` : ""}</p>
              </div>
              {d.published?.review_status === "REVIEWED"
                ? <span className="text-xs font-semibold text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> geprüft</span>
                : <span className="text-xs font-semibold text-amber-600">ungeprüft</span>}
              <button data-testid={`content-edit-${d.slug}`} onClick={() => setEditing(d.slug)} className={`${btn} border border-border hover:bg-secondary`}>Bearbeiten</button>
            </div>
          ))}
        </div>
      </div>
      {editing && <Editor slug={editing} onClose={() => setEditing(null)} onSaved={load} />}
      <ImprintCard />
      <ConfigCard />
    </div>
  );
}
